//DashboardWatchlist.tsx
'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Database,
  RefreshCw,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { Tooltip } from '@/components/ui/tooltip';
import { useDashboard } from '@/contexts/DashboardContext';
import { WatchlistItem } from '@/src/components/component/dashboard/watchlist/watchlist.types';
import { RolexImage } from '@/utils/RolexImage';
import { ChartToggle } from './ChartToggle';

function getCurrentPrice(
  symbol: string,
  items: WatchlistItem[] = []
): number | null {
  if (!symbol) return null;

  const item = items.find((item) => item.symbol === symbol);
  if (item && item.last) {
    return item.last;
  }

  return Math.floor(Math.random() * 20000) + 5000; // Fallback random price
}

function getIconPath(
  symbol: string,
  imagePaths?: Record<string, string>
): string {
  if (imagePaths && imagePaths[symbol]) {
    return imagePaths[symbol];
  }

  const brand = symbol.split(':')[0].toLowerCase();

  const iconMap: Record<string, string> = {
    rolex: '/rolex.svg',
    omega: '/omega.svg',
    ap: '/ap.webp',
    patek: '/nautilus.jpg',
    default: '/watchinspect.svg',
  };

  return iconMap[brand] || iconMap.default;
}

interface WatchData {
  symbol: string;
  displayName: string;
  fullName?: string;
  description?: string;
  source?: 'forum' | 'reddit' | 'ebay' | string;
  lastPrice?: number;
  medianPrice?: number;
  priceChange?: number;
  volume?: number;
  dataCount?: number;
  exchange?: string;
}

interface SymbolInfo {
  name: string;
  description?: string;
  exchange?: string;
  price?: number;
}

interface DashboardWatchlistProps {
  selectedSymbol: SymbolInfo;
  onSymbolSelect: (symbol: string) => void;
  mobile?: boolean;
  tvWidget?: any;
  activeChart?: 'tradingview' | 'recharts';
  onChartTypeChange?: (chartType: 'tradingview' | 'recharts') => void;
}

export function DashboardWatchlist({
  selectedSymbol,
  onSymbolSelect,
  mobile = false,
  tvWidget,
  activeChart = 'tradingview',
  onChartTypeChange,
}: DashboardWatchlistProps) {
  const {
    state,
    dispatch,
    changeSource,
    datafeed,
    sourceAvailability,
    watchlistItems,
    setWatchlistItems,
  } = useDashboard();

  const [searchValue, setSearchValue] = useState('');
  const [watches, setWatches] = useState<WatchData[]>([]);
  const [activeSource, setActiveSource] = useState<
    'all' | 'forum' | 'reddit' | 'ebay'
  >(state.source as any);
  const [selectedWatch, setSelectedWatch] = useState<WatchData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadWatchData = useCallback(
    async (source?: string) => {
      setIsLoading(true);
      try {
        const response = await fetch('/api/trading-dashboard/watchData');

        if (!response.ok) {
          throw new Error(`Failed to fetch watch data: ${response.status}`);
        }

        const data = await response.json();

        if (data && Array.isArray(data.data)) {
          // Filter by source if specified
          let filteredData = data.data;
          if (source && source !== 'all') {
            filteredData = data.data.filter(
              (item: any) => item.source === source || !item.source
            );
          }

          // Map API data to our format
          let watchesData: WatchData[] = filteredData.map((item: any) => {
            // Generate a display price and change based on dataCount to make it look realistic
            const basePrice = item.symbol.includes('DAYTONA')
              ? 35000
              : item.symbol.includes('SUBMARINER') ||
                  item.symbol.includes('16610')
                ? 12500
                : item.symbol.includes('GMT')
                  ? 18500
                  : 10000;

            const priceOffset = Math.random() * 1000 - 500;
            const changeDirection = Math.random() > 0.5 ? 1 : -1;
            const changeAmount =
              Math.floor(Math.random() * 500) * changeDirection;

            return {
              symbol: item.symbol,
              displayName: item.description || item.fullName || item.symbol,
              fullName: item.fullName,
              description: item.description,
              source: item.source || 'forum',
              exchange: item.exchange,
              lastPrice: basePrice + priceOffset,
              priceChange: changeAmount,
              volume: item.dataCount || Math.floor(Math.random() * 20) + 1,
              dataCount: item.dataCount,
            };
          });

          // Sort by data count or relevance first
          watchesData.sort((a, b) => (b.dataCount || 0) - (a.dataCount || 0));

          // Select top items but ensure important models are included
          const importantModels = [
            'ROLEX:INDEX',
            'ROLEX:16610',
            'ROLEX:DAYTONA',
            'OMEGA:SPEEDMASTER',
          ];

          // First make sure we have the important models
          let selectedWatches: WatchData[] = [];

          // Add ROLEX:INDEX if not present
          const hasRolexIndex = watchesData.some(
            (item) => item.symbol === 'ROLEX:INDEX'
          );
          if (!hasRolexIndex) {
            selectedWatches.push({
              symbol: 'ROLEX:INDEX',
              displayName: 'Rolex Market Index',
              fullName: 'Rolex Market Index',
              description: 'Composite index of top 30 Rolex models',
              source: 'index',
              exchange: 'WatchInspect',
              lastPrice: 14800,
              priceChange: 350,
              volume: 30,
              dataCount: 9999,
            });
          }

          // Add other important models that exist in our data
          importantModels.forEach((model) => {
            if (model === 'ROLEX:INDEX' && hasRolexIndex) {
              const indexModel = watchesData.find(
                (item) => item.symbol === 'ROLEX:INDEX'
              );
              if (indexModel) {
                selectedWatches.push(indexModel);
              }
            } else {
              const foundModel = watchesData.find(
                (item) => item.symbol === model
              );
              if (
                foundModel &&
                !selectedWatches.some((item) => item.symbol === model)
              ) {
                selectedWatches.push(foundModel);
              }
            }
          });

          // Add more models until we reach 8 total items or run out of watches
          const remainingWatches = watchesData.filter(
            (watch) =>
              !selectedWatches.some(
                (selected) => selected.symbol === watch.symbol
              )
          );

          // Fill remaining slots up to 8 items total
          const maxTotal = 8;
          while (
            selectedWatches.length < maxTotal &&
            remainingWatches.length > 0
          ) {
            selectedWatches.push(remainingWatches.shift()!);
          }

          // Sort the final selection by data count
          selectedWatches.sort(
            (a, b) => (b.dataCount || 0) - (a.dataCount || 0)
          );

          setWatches(selectedWatches);

          // Create watchlist items with our final selection
          const watchlistItems = selectedWatches.map((watch) => ({
            symbol: watch.symbol,
            name: watch.displayName,
            last: watch.lastPrice || 0,
            change: watch.priceChange || 0,
            changePercent: watch.lastPrice
              ? ((watch.priceChange || 0) / watch.lastPrice) * 100
              : 0,
            icon: getIconPath(watch.symbol),
            subModel: watch.source,
            brand: getBrandFromSymbol(watch.symbol),
            model: watch.displayName,
          }));

          console.log(
            `Watch data loaded with ${watchlistItems.length} items including ROLEX:INDEX`
          );
          setWatchlistItems(watchlistItems);
        } else {
          console.warn('Invalid data format from API:', data);
          // Use mock data as fallback
          console.warn('using mock data - test');

          // Use a limited set of mock data
          const mockData = getMockWatchData().slice(0, 8); // Limit to top 8 items
          setWatches(mockData);

          // Update context with mock data
          const watchlistItems = mockData.map((watch) => ({
            symbol: watch.symbol,
            name: watch.displayName,
            last: watch.lastPrice || 0,
            change: watch.priceChange || 0,
            changePercent: watch.lastPrice
              ? ((watch.priceChange || 0) / watch.lastPrice) * 100
              : 0,
            icon: getIconPath(watch.symbol),
            subModel: watch.source,
            brand: getBrandFromSymbol(watch.symbol),
            model: watch.displayName,
          }));

          setWatchlistItems(watchlistItems);
        }
      } catch (error) {
        console.error('Error loading watch data:', error);
        // Use mock data on error
        console.warn('using mock data on error- test');

        // Use a limited set of mock data
        const fallbackData = getMockWatchData().slice(0, 8); // Limit to top 8 items
        setWatches(fallbackData);

        // Update context with fallback data
        const watchlistItems = fallbackData.map((watch) => ({
          symbol: watch.symbol,
          name: watch.displayName,
          last: watch.lastPrice || 0,
          change: watch.priceChange || 0,
          changePercent: watch.lastPrice
            ? ((watch.priceChange || 0) / watch.lastPrice) * 100
            : 0,
          icon: getIconPath(watch.symbol),
          subModel: watch.source,
          brand: getBrandFromSymbol(watch.symbol),
          model: watch.displayName,
        }));

        setWatchlistItems(watchlistItems);
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [setWatchlistItems]
  );

  // Initial data load and when active source changes
  useEffect(() => {
    loadWatchData(activeSource === 'all' ? undefined : activeSource);
  }, [loadWatchData, activeSource]);

  // Update local state when context source changes
  useEffect(() => {
    setActiveSource(state.source as any);
  }, [state.source]);

  // **** CRITICAL FIX: This is the main function that handles watch selection
  const handleWatchSelect = (watch: WatchData) => {
    console.log(`DashboardWatchlist: Watch selected: ${watch.symbol}`);
    setSelectedWatch(watch);

    // APPROACH 1: Use window.tvWidget directly - this is working in your old watchlist
    const updateWithWindowTvWidget = () => {
      if (typeof window !== 'undefined' && window.tvWidget) {
        try {
          // Get current interval from widget - EXACT PATTERN from working watchlist
          const interval = window.tvWidget.symbolInterval?.()?.interval || '1D';

          console.log(
            `DashboardWatchlist: Using window.tvWidget to update to ${watch.symbol} with interval ${interval}`
          );

          // THIS EXACT PATTERN works in the original watchlist
          window.tvWidget.setSymbol('', interval, () => {
            // Important: Wait 500ms before setting the new symbol (same as original)
            setTimeout(() => {
              window.tvWidget.setSymbol(watch.symbol, interval, () => {
                console.log(
                  `Chart symbol updated to ${watch.symbol} via window.tvWidget`
                );

                // Dispatch event to notify other components of the change
                try {
                  const event = new CustomEvent('tradingview-symbol-change', {
                    detail: { symbol: watch.symbol },
                    bubbles: true,
                  });
                  window.dispatchEvent(event);
                } catch (e) {
                  console.error('Error dispatching symbol change event:', e);
                }
              });
            }, 500);
          });
          return true;
        } catch (err) {
          console.error('Error with window.tvWidget:', err);
          return false;
        }
      }
      return false;
    };

    // APPROACH 2: Use the tvWidget prop
    const updateWithTvWidgetProp = () => {
      if (tvWidget) {
        try {
          const interval = tvWidget.symbolInterval?.()?.interval || '1D';
          console.log(
            `DashboardWatchlist: Using tvWidget prop with interval ${interval}`
          );

          // Use the same pattern
          tvWidget.setSymbol('', interval, () => {
            setTimeout(() => {
              tvWidget.setSymbol(watch.symbol, interval, () => {
                console.log(
                  `Chart symbol updated to ${watch.symbol} via tvWidget prop`
                );
              });
            }, 500);
          });
          return true;
        } catch (err) {
          console.error('Error with tvWidget prop:', err);
          return false;
        }
      }
      return false;
    };

    // APPROACH 3: Use the global helper function if available
    const updateWithGlobalHelper = () => {
      if (
        typeof window !== 'undefined' &&
        typeof window.forceChartSymbolUpdate === 'function'
      ) {
        try {
          console.log(
            `DashboardWatchlist: Using global helper function for ${watch.symbol}`
          );
          return window.forceChartSymbolUpdate(watch.symbol);
        } catch (err) {
          console.error('Error with global helper:', err);
          return false;
        }
      }
      return false;
    };

    // APPROACH 4: Last resort - dispatch a custom event
    const dispatchSymbolChangeEvent = () => {
      try {
        const event = new CustomEvent('watch-symbol-selected', {
          detail: { symbol: watch.symbol },
          bubbles: true,
        });
        window.dispatchEvent(event);
        document.dispatchEvent(event);
        console.log(
          `DashboardWatchlist: Dispatched watch-symbol-selected event for ${watch.symbol}`
        );
        return true;
      } catch (e) {
        console.error('Error dispatching event:', e);
        return false;
      }
    };

    // Try all approaches in sequence
    const directUpdateSucceeded =
      updateWithWindowTvWidget() ||
      updateWithTvWidgetProp() ||
      updateWithGlobalHelper() ||
      dispatchSymbolChangeEvent();

    if (!directUpdateSucceeded) {
      console.warn('All direct chart update methods failed.');
    }

    // Always call the parent's onSymbolSelect to update context
    onSymbolSelect(watch.symbol);
  };

  // Filter watches based on search and active source
  const filteredWatches = watches.filter((watch) => {
    const matchesSearch =
      searchValue === '' ||
      watch.displayName.toLowerCase().includes(searchValue.toLowerCase()) ||
      watch.symbol.toLowerCase().includes(searchValue.toLowerCase());

    const matchesSource =
      activeSource === 'all' ||
      !watch.source || // Include entries without source in all tabs
      (activeSource === 'forum' && watch.source === 'forum') ||
      (activeSource === 'reddit' && watch.source === 'reddit') ||
      (activeSource === 'ebay' && watch.source === 'ebay');

    return matchesSearch && matchesSource;
  });

  // Format price change
  const formatPriceChange = (change: number, isPercent = false) => {
    if (isPercent) {
      return `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
    }
    return `${change >= 0 ? '+' : ''}$${Math.abs(change).toLocaleString('en-US')}`;
  };

  // Calculate color based on price change
  const getPriceChangeColor = (change: number) => {
    if (change > 0) return 'text-green-500';
    if (change < 0) return 'text-red-500';
    return 'text-slate-400';
  };

  // Handle source change
  const handleSourceChange = (
    newSource: 'all' | 'forum' | 'reddit' | 'ebay'
  ) => {
    setIsRefreshing(true);
    setActiveSource(newSource);

    // Update the application-wide source in context
    dispatch({ type: 'SET_SOURCE', payload: newSource });

    // Only call changeSource for non-'all' sources
    if (newSource !== 'all') {
      changeSource(newSource);

      // Force chart refresh if needed - try with window.tvWidget first
      if (typeof window !== 'undefined' && window.tvWidget) {
        try {
          const currentSymbol =
            window.tvWidget.symbolInterval?.()?.symbol || selectedSymbol.name;
          const interval = window.tvWidget.symbolInterval?.()?.interval || '1D';

          console.log(
            `Source change - reloading symbol ${currentSymbol} with interval ${interval}`
          );

          // Reset symbol to force reload using direct window access
          window.tvWidget.setSymbol('', interval, () => {
            // Then set it back with a delay
            setTimeout(() => {
              window.tvWidget.setSymbol(currentSymbol, interval, () => {
                console.log(
                  `Symbol reloaded for source change to ${newSource}`
                );
              });
            }, 500);
          });
        } catch (err) {
          console.error('Error refreshing chart for source change:', err);

          // Try with prop as fallback
          if (tvWidget) {
            try {
              const currentSymbol =
                tvWidget.symbolInterval?.()?.symbol || selectedSymbol.name;
              const interval = tvWidget.symbolInterval?.()?.interval || '1D';

              tvWidget.setSymbol('', interval, () => {
                setTimeout(() => {
                  tvWidget.setSymbol(currentSymbol, interval);
                }, 500);
              });
            } catch (e) {
              console.error('Error with tvWidget prop for source change:', e);
            }
          }
        }
      }
      // If window.tvWidget doesn't exist, try with component prop
      else if (tvWidget) {
        try {
          const currentSymbol =
            tvWidget.symbolInterval?.()?.symbol || selectedSymbol.name;
          const interval = tvWidget.symbolInterval?.()?.interval || '1D';

          tvWidget.setSymbol('', interval, () => {
            setTimeout(() => {
              tvWidget.setSymbol(currentSymbol, interval);
            }, 500);
          });
        } catch (e) {
          console.error('Error with tvWidget prop for source change:', e);
        }
      }
    }

    // Reload data with new source
    loadWatchData(newSource === 'all' ? undefined : newSource);
  };

  return (
    <div className="h-full flex flex-col justify-between bg-slate-900/20 backdrop-blur-sm">
      {/* Header */}
      <div className="p-3 border-b border-slate-800 bg-gradient-to-r from-slate-900 to-slate-800/90">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white flex items-center">
            <BarChart3 className="h-4 w-4 mr-1.5 text-blue-400" />
            Watch Price Tracker
          </h3>

          <Tabs defaultValue={activeSource} className="w-auto">
            <TabsList className="bg-slate-800/70 p-0.5 h-7 shadow-inner shadow-black/10">
              <TabsTrigger
                value="all"
                className="px-2 py-0.5 h-6 text-xs data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
                onClick={() => handleSourceChange('all')}
              >
                All
              </TabsTrigger>
              <TabsTrigger
                value="forum"
                className="px-2 py-0.5 h-6 text-xs data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
                onClick={() => handleSourceChange('forum')}
              >
                Forum
              </TabsTrigger>
              <TabsTrigger
                value="reddit"
                className="px-2 py-0.5 h-6 text-xs data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
                onClick={() => handleSourceChange('reddit')}
                disabled={!sourceAvailability.reddit}
              >
                Reddit
              </TabsTrigger>
              <TabsTrigger
                value="ebay"
                className="px-2 py-0.5 h-6 text-xs data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
                onClick={() => handleSourceChange('ebay')}
                disabled={!sourceAvailability.ebay}
              >
                eBay
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Add the ChartToggle component here */}
        {onChartTypeChange && (
          <div className="mb-3">
            <ChartToggle
              activeChart={activeChart}
              onToggle={onChartTypeChange}
              className="w-full"
            />
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search watches..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="pl-9 py-2 h-9 bg-slate-800/70 border-slate-700/50 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-all"
          />
        </div>
      </div>

      {/* Column Headers */}
      <div className="px-3 py-2 border-b border-slate-800/50 bg-slate-800/30 text-xs text-slate-400 grid grid-cols-3 gap-2">
        <div>Model</div>
        <div className="text-center">Price</div>
        <div className="text-right">24h Change</div>
      </div>

      {/* Watch List */}
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
          </div>
        ) : filteredWatches.length === 0 ? (
          <div className="p-4 text-center text-slate-400 text-sm">
            {searchValue
              ? 'No matching watches found'
              : 'No watches available for this source'}
          </div>
        ) : (
          <div className="p-2">
            {filteredWatches.map((watch, index) => (
              <motion.div
                key={watch.symbol}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
              >
                <Button
                  variant="ghost"
                  className={`
                    w-full justify-between px-3 py-3 mb-1.5 rounded-lg
                    hover:bg-slate-800/60 hover:text-blue-400 hover:shadow-md hover:shadow-black/10
                    ${
                      selectedSymbol.name === watch.symbol
                        ? 'bg-gradient-to-r from-slate-800/90 to-slate-800/60 text-blue-400 border-l-2 border-blue-500 pl-[10px] shadow-md shadow-black/10'
                        : 'text-slate-300'
                    }
                    transition-all duration-200
                  `}
                  onClick={() => handleWatchSelect(watch)}
                >
                  <div className="flex items-center">
                    <span className="flex items-center justify-center h-8 w-8 rounded-full bg-slate-800 mr-2 border border-slate-700/50">
                      {(() => {
                        // Get model number if it's a Rolex
                        const brand = getBrandFromSymbol(watch.symbol);
                        if (brand.toLowerCase() === 'rolex') {
                          const modelNumber = watch.symbol.split(':')[1];
                          return (
                            <div className="flex items-center justify-center">
                              <RolexImage
                                modelNumber={modelNumber}
                                size={16}
                                className="rounded-full"
                              />
                            </div>
                          );
                        } else {
                          // For other brands use standard image
                          return (
                            <Image
                              src={`/${brand.toLowerCase()}.svg`}
                              width={16}
                              height={16}
                              alt={brand}
                              onError={(e) => {
                                e.currentTarget.src = '/watchinspect.svg';
                              }}
                            />
                          );
                        }
                      })()}
                    </span>
                    <div className="text-left">
                      <div className="font-medium text-sm truncate max-w-[120px]">
                        {watch.displayName}
                      </div>
                      <div className="text-xs text-slate-400 truncate max-w-[120px] flex items-center">
                        <Badge
                          variant="outline"
                          className="px-1 text-[10px] border-slate-700 text-slate-300 mr-1"
                        >
                          {getBrandFromSymbol(watch.symbol)}
                        </Badge>
                        {watch.source && (
                          <Badge
                            variant="outline"
                            className="px-1 text-[10px] border-slate-700 bg-slate-800/50 text-slate-300 ml-1"
                          >
                            {watch.source}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="font-medium text-sm">
                      ${watch.lastPrice?.toLocaleString('en-US') || 'N/A'}
                    </div>
                    <div
                      className={`
                      text-xs flex items-center justify-end
                      ${getPriceChangeColor(watch.priceChange || 0)}
                    `}
                    >
                      {(watch.priceChange || 0) > 0 ? (
                        <span className="flex items-center justify-center bg-green-500/10 rounded-full p-0.5 mr-1">
                          <ArrowUpRight className="h-3 w-3" />
                        </span>
                      ) : (watch.priceChange || 0) < 0 ? (
                        <span className="flex items-center justify-center bg-red-500/10 rounded-full p-0.5 mr-1">
                          <ArrowDownRight className="h-3 w-3" />
                        </span>
                      ) : null}
                      {formatPriceChange(watch.priceChange || 0, false)}
                    </div>
                  </div>
                </Button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Market summary */}
      <div className="mt-auto border-t border-slate-800/50 bg-slate-800/80 backdrop-blur-sm z-20">
        <div className="grid grid-cols-3 gap-2 text-center p-2">
          <div className="bg-slate-800/70 rounded-md p-2">
            <div className="text-xs text-slate-400">Most Active</div>
            <div className="text-sm text-white font-medium">Submariner</div>
          </div>
          <div className="bg-slate-800/30 rounded-md p-2 flex flex-col items-center justify-center">
            <div className="text-xs text-slate-400">Data Source</div>
            <div className="text-sm text-blue-400 font-medium flex items-center">
              <Database className="h-3 w-3 mr-1" />
              {activeSource === 'all' ? 'All Sources' : activeSource}
            </div>
          </div>
          <div className="bg-slate-800/30 rounded-md p-2">
            <div className="text-xs text-slate-400">Volume</div>
            <div className="text-sm text-white font-medium">
              {isRefreshing ? (
                <span className="flex items-center justify-center">
                  <RefreshCw className="h-3 w-3 animate-spin mr-1" /> Loading...
                </span>
              ) : (
                `${getTotalVolume(filteredWatches)} listings`
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add New Watch Button */}
      <div className="pt-2 border-t border-slate-800 bg-slate-950 z-30 pb-3">
        <Tooltip content="Add a new watch to track prices">
          <Button
            variant="outline"
            className="w-full bg-blue-600/10 text-blue-400 border-blue-500/30 hover:bg-blue-600/20 shadow-md shadow-blue-900/10"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Watch to Track
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

function getBrandFromSymbol(symbol: string): string {
  if (!symbol) return 'Watch';

  // If symbol contains a colon (e.g. "ROLEX:16610"), extract the brand part
  if (symbol.includes(':')) {
    return symbol.split(':')[0];
  }

  // Default to Watch
  return 'Watch';
}

// Helper to get total volume across all watches
function getTotalVolume(watches: WatchData[]): number {
  return watches.reduce((sum, watch) => sum + (watch.volume || 0), 0);
}

// Mock data as fallback
function getMockWatchData(): WatchData[] {
  return [
    {
      symbol: 'ROLEX:INDEX',
      displayName: 'Rolex Market Index',
      source: 'index',
      lastPrice: 14800,
      medianPrice: 14500,
      priceChange: 350,
      volume: 30,
      exchange: 'WatchInspect',
      description: 'Rolex Market Index',
    },
    {
      symbol: 'ROLEX:16610',
      displayName: 'Submariner 16610',
      source: 'forum',
      lastPrice: 12500,
      medianPrice: 12700,
      priceChange: 250,
      volume: 12,
    },
    {
      symbol: 'ROLEX:16610',
      displayName: 'Submariner 16610',
      source: 'forum',
      lastPrice: 12500,
      medianPrice: 12700,
      priceChange: 250,
      volume: 12,
    },
    {
      symbol: 'ROLEX:126610',
      displayName: 'Submariner 126610',
      source: 'forum',
      lastPrice: 18700,
      medianPrice: 18500,
      priceChange: -150,
      volume: 8,
    },
    {
      symbol: 'ROLEX:DAYTONA',
      displayName: 'Daytona',
      source: 'reddit',
      lastPrice: 31000,
      medianPrice: 30000,
      priceChange: 1800,
      volume: 5,
    },
    {
      symbol: 'OMEGA:SPEEDMASTER',
      displayName: 'Speedmaster',
      source: 'ebay',
      lastPrice: 6500,
      medianPrice: 6200,
      priceChange: 100,
      volume: 22,
    },
    {
      symbol: 'AP:ROYALOAK',
      displayName: 'Royal Oak',
      source: 'forum',
      lastPrice: 45000,
      medianPrice: 43500,
      priceChange: 2500,
      volume: 3,
    },
  ];
}

export default DashboardWatchlist;
